import { inject, Injectable, resource } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import { toFormData } from '../../../utils/helper';
import { ToolchainJob } from '../../../utils/models/toolchain.models';
import { firstValueFrom } from 'rxjs';
import { CapabilityStore } from '../../capability.store';

/**
 * Service for interacting with toolchain jobs.
 */
@Injectable({
  providedIn: 'root',
})
export class ToolchainJobService {
  /** Inject services. */
  private readonly httpService = inject(HttpClient);
  private readonly capabilityStore = inject(CapabilityStore);

  getJobById(toolchainId: number, jobId: number | undefined) {
    const history = this.capabilityStore.toolchainJobHistory(toolchainId);
    const cachedJob = history.items().find(job => job.toolchainJobId === jobId);
    let previousStatus = cachedJob?.status;

    return resource<ToolchainJob | undefined, number | undefined>({
      defaultValue: cachedJob,
      params: () => jobId,
      loader: async ({ params }) => {
        const freshJob = await firstValueFrom(
          this.httpService.get<ToolchainJob>(`external/toolchains/jobs/${params}`)
        );
        if (previousStatus && previousStatus !== freshJob.status) history.refresh();
        previousStatus = freshJob.status;
        return freshJob;
      },
    });
  }

  // starts immediately first step of the chain
  async createJob(toolchainId: number, toolInputData: FormGroup) {
    // Change FormGroup to FormData to pass files and form values
    const formData = toFormData(toolInputData);
    formData.append('toolchain_id', toolchainId.toString());
    const res = this.httpService.post<{ toolchainJobId: number }>(
      'external/toolchains/jobs',
      formData
    );
    const result = await firstValueFrom(res);
    this.capabilityStore.refreshToolchainJobHistory(toolchainId);
    return result;
  }

  async triggerNextStep(toolchainId: number, jobId: number, toolInputData: FormGroup) {
    // Change FormGroup to FormData to pass files and form values
    const formData = toFormData(toolInputData);
    const res = this.httpService.post<{ toolJobId: number }>(
      `external/toolchains/jobs/${jobId}`,
      formData
    );
    const result = await firstValueFrom(res);
    this.capabilityStore.refreshToolchainJobHistory(toolchainId);
    return result;
  }

  restoreStep(jobId: number, stepNo: number) {
    const res = this.httpService.post<{ stepNo: number }>(
      `external/toolchains/jobs/${jobId}/${stepNo}`,
      {}
    );
    return firstValueFrom(res);
  }

  async deleteToolchainJob(toolchainId: number, jobId: number) {
    const res = this.httpService.delete<{ success: boolean; msg: string }>(
      `external/toolchains/jobs/${jobId}`
    );
    const result = await firstValueFrom(res);
    this.capabilityStore.refreshToolchainJobHistory(toolchainId);
    return result;
  }
}
